/**
 * PipelineExecutionView — Compact overlay card for pipeline execution status.
 *
 * Positioned as an absolute overlay (bottom-right, 280px wide) on the DAG canvas.
 * Shows step list with status icons, costs, elapsed time, and row progress.
 * Failed steps show error details + Retry / Fix & Retry actions.
 * Failed pipeline shows Resume + Re-run All buttons.
 */
import React from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CompletedIcon,
  Error as FailedIcon,
  HourglassEmpty as PendingIcon,
  PlayCircle as RunningIcon,
  PauseCircle as PausedIcon,
  Transform as TransformIcon,
  Code as CodeIcon,
  CompareArrows as MatchIcon,
  Verified as GateIcon,
  Psychology as LlmIcon,
  Api as ApiIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Replay as ReplayIcon,
  Build as FixIcon,
  PlayArrow as ResumeIcon,
} from '@mui/icons-material';

export interface StepProgress {
  rows_processed: number;
  total_rows: number;
  elapsed_seconds: number;
}

interface PipelineStep {
  step_id: string;
  step_type: string;
  title: string;
  status: string;
  step_order: number;
  output_ref?: string;
  input_refs: string[];
  config_summary?: string;
  actual_cost?: number;
  error?: string;
}

interface PipelineInfo {
  pipeline_id: string;
  status: string;
  total_steps: number;
  steps: PipelineStep[];
  total_cost_usd?: number;
}

interface PipelineExecutionViewProps {
  pipeline: PipelineInfo | null;
  stepProgress?: Record<string, StepProgress>;
  onRerun?: (pipelineId: string) => void;
  /** Retry a single failed step (sends message to agent). */
  onStepRetry?: (stepId: string, stepTitle: string) => void;
  /** Open the failed step in the inspector for code editing. */
  onStepFix?: (stepId: string) => void;
  /** Resume pipeline from last failed/paused step. */
  onResume?: () => void;
}

const STEP_ICONS: Record<string, React.ReactElement> = {
  sql_transform: <TransformIcon sx={{ fontSize: 14 }} />,
  python_transform: <CodeIcon sx={{ fontSize: 14 }} />,
  fuzzy_match: <MatchIcon sx={{ fontSize: 14 }} />,
  llm_extract: <LlmIcon sx={{ fontSize: 14 }} />,
  llm_classify: <LlmIcon sx={{ fontSize: 14 }} />,
  api_enrich: <ApiIcon sx={{ fontSize: 14 }} />,
  validation_gate: <GateIcon sx={{ fontSize: 14 }} />,
};

const STATUS_ICONS: Record<string, React.ReactElement> = {
  completed: <CompletedIcon sx={{ fontSize: 14, color: 'success.main' }} />,
  failed: <FailedIcon sx={{ fontSize: 14, color: 'error.main' }} />,
  pending: <PendingIcon sx={{ fontSize: 14, color: 'text.disabled' }} />,
  running: <RunningIcon sx={{ fontSize: 14, color: 'info.main' }} />,
  awaiting_review: <PausedIcon sx={{ fontSize: 14, color: 'warning.main' }} />,
};

const PIPELINE_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  approved: 'Ready to Run',
  running: 'Executing...',
  completed: 'Completed',
  failed: 'Failed',
  paused: 'Paused at Gate',
};

function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

function CompactStepItem({
  step,
  progress,
  onRetry,
  onFix,
}: {
  step: PipelineStep;
  progress?: StepProgress;
  onRetry?: (stepId: string, stepTitle: string) => void;
  onFix?: (stepId: string) => void;
}) {
  const icon = STEP_ICONS[step.step_type] || <TransformIcon sx={{ fontSize: 14 }} />;
  const statusIcon = STATUS_ICONS[step.status] || STATUS_ICONS.pending;
  const isRunning = step.status === 'running';
  const isFailed = step.status === 'failed';
  const stepTitle = step.title || step.step_type.replace(/_/g, ' ');

  return (
    <>
      <ListItem
        dense
        disablePadding
        sx={{
          px: 1.5,
          py: 0.25,
          ...(isRunning && {
            bgcolor: 'action.hover',
            borderLeft: '3px solid',
            borderColor: 'info.main',
          }),
          ...(isFailed && {
            bgcolor: alpha('#ef4444', 0.06),
            borderLeft: '3px solid',
            borderColor: 'error.main',
          }),
        }}
      >
        <ListItemIcon sx={{ minWidth: 22 }}>{icon}</ListItemIcon>
        <ListItemText
          primary={
            <Typography variant="caption" noWrap sx={{ fontSize: 11, maxWidth: 140 }}>
              {stepTitle}
            </Typography>
          }
          secondary={
            isRunning && progress && progress.total_rows > 0 ? (
              <Box sx={{ mt: 0.25 }}>
                <LinearProgress
                  variant="determinate"
                  value={(progress.rows_processed / progress.total_rows) * 100}
                  sx={{ height: 3, borderRadius: 1.5 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
                  {progress.rows_processed.toLocaleString()}/{progress.total_rows.toLocaleString()} rows
                  {' \u00b7 '}{formatElapsed(progress.elapsed_seconds)}
                </Typography>
              </Box>
            ) : isRunning && progress ? (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
                {formatElapsed(progress.elapsed_seconds)}
              </Typography>
            ) : undefined
          }
        />
        <Stack direction="row" spacing={0.25} alignItems="center">
          {step.actual_cost != null && step.actual_cost > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
              ${step.actual_cost.toFixed(3)}
            </Typography>
          )}
          {statusIcon}
        </Stack>
      </ListItem>

      {/* Failed step: show error + action buttons */}
      {isFailed && (
        <Box sx={{ px: 1.5, py: 0.5, bgcolor: alpha('#ef4444', 0.04) }}>
          {step.error && (
            <Typography
              variant="caption"
              color="error.main"
              sx={{
                fontSize: 9,
                display: 'block',
                mb: 0.5,
                lineHeight: 1.3,
                whiteSpace: 'pre-wrap',
                maxHeight: 40,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {step.error.length > 120 ? `${step.error.slice(0, 120)}…` : step.error}
            </Typography>
          )}
          <Stack direction="row" spacing={0.5}>
            {onRetry && (
              <Tooltip title="Retry this step">
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<ReplayIcon sx={{ fontSize: 11 }} />}
                  onClick={(e) => { e.stopPropagation(); onRetry(step.step_id, stepTitle); }}
                  sx={{ fontSize: 9, py: 0, px: 1, minHeight: 22, textTransform: 'none', borderRadius: 1 }}
                >
                  Retry
                </Button>
              </Tooltip>
            )}
            {onFix && (
              <Tooltip title="Open in inspector to edit code">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FixIcon sx={{ fontSize: 11 }} />}
                  onClick={(e) => { e.stopPropagation(); onFix(step.step_id); }}
                  sx={{ fontSize: 9, py: 0, px: 1, minHeight: 22, textTransform: 'none', borderRadius: 1 }}
                >
                  Fix
                </Button>
              </Tooltip>
            )}
          </Stack>
        </Box>
      )}
    </>
  );
}

const PipelineExecutionView: React.FC<PipelineExecutionViewProps> = ({
  pipeline,
  stepProgress,
  onRerun,
  onStepRetry,
  onStepFix,
  onResume,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = React.useState(true);

  if (!pipeline) return null;

  const { steps, status, total_cost_usd } = pipeline;
  const completedSteps = steps.filter((s) => s.status === 'completed').length;
  const failedSteps = steps.filter((s) => s.status === 'failed').length;
  const progress = steps.length > 0 ? (completedSteps / steps.length) * 100 : 0;
  const statusLabel = PIPELINE_STATUS_LABELS[status] || status;
  const canRerun = status === 'completed' || status === 'failed';
  const canResume = status === 'failed' || status === 'paused';

  const pipelineStatusColor =
    status === 'completed'
      ? 'success'
      : status === 'failed'
        ? 'error'
        : status === 'running'
          ? 'info'
          : 'default';

  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        maxHeight: 380,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Compact header */}
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          borderBottom: expanded ? 1 : 0,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(8px)',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>
              Pipeline
            </Typography>
            <Chip
              label={statusLabel}
              size="small"
              color={pipelineStatusColor as any}
              sx={{ fontSize: '0.6rem', height: 18 }}
            />
          </Stack>
          <Stack direction="row" spacing={0.25} alignItems="center">
            {/* Resume button (primary action when failed/paused) */}
            {canResume && onResume && (
              <Tooltip title="Resume pipeline from where it stopped">
                <IconButton
                  size="small"
                  color="primary"
                  sx={{ p: 0.25 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onResume();
                  }}
                >
                  <ResumeIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            {/* Re-run button (full re-execution) */}
            {canRerun && onRerun && (
              <Tooltip title={canResume ? 'Re-run entire pipeline from start' : 'Re-run pipeline on current data'}>
                <IconButton
                  size="small"
                  sx={{ p: 0.25 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRerun(pipeline.pipeline_id);
                  }}
                >
                  <ReplayIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" sx={{ p: 0.25 }}>
              {expanded ? <CollapseIcon sx={{ fontSize: 16 }} /> : <ExpandIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Stack>
        </Stack>

        {/* Progress bar */}
        {(status === 'running' || completedSteps > 0) && (
          <Box sx={{ mt: 0.5 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              color={failedSteps > 0 ? 'error' : 'primary'}
              sx={{ height: 3, borderRadius: 1.5 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block', fontSize: 10 }}>
              {completedSteps}/{steps.length} steps
              {total_cost_usd != null && total_cost_usd > 0 && ` \u2022 $${total_cost_usd.toFixed(4)}`}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Collapsible step list */}
      <Collapse in={expanded}>
        <List dense disablePadding sx={{ maxHeight: 250, overflow: 'auto' }}>
          {steps
            .sort((a, b) => a.step_order - b.step_order)
            .map((step) => (
              <CompactStepItem
                key={step.step_id}
                step={step}
                progress={stepProgress?.[step.step_id]}
                onRetry={onStepRetry}
                onFix={onStepFix}
              />
            ))}
        </List>

        {/* Resume/Rerun actions footer when pipeline failed */}
        {canResume && (onResume || onRerun) && (
          <Box
            sx={{
              px: 1.5,
              py: 0.75,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              gap: 0.75,
            }}
          >
            {onResume && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<ResumeIcon sx={{ fontSize: 13 }} />}
                onClick={(e) => { e.stopPropagation(); onResume(); }}
                sx={{ fontSize: 10, py: 0.25, px: 1.5, textTransform: 'none', borderRadius: 1.5, flex: 1 }}
              >
                Resume
              </Button>
            )}
            {onRerun && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<ReplayIcon sx={{ fontSize: 13 }} />}
                onClick={(e) => { e.stopPropagation(); onRerun(pipeline.pipeline_id); }}
                sx={{ fontSize: 10, py: 0.25, px: 1.5, textTransform: 'none', borderRadius: 1.5, flex: 1 }}
              >
                Re-run All
              </Button>
            )}
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};

export default PipelineExecutionView;
